import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { API_BACKEND_URL } from "../Config/Config";

/**
 * Requirements documents attached to an API collection.
 *
 * Whatever is attached here is fed to test-case and chain generation for that collection, so the
 * generated output addresses what the client asked for and not only what the contract describes.
 */

const fetchRequirements = async ({ queryKey }) => {
  const [, collectionId, companyId] = queryKey;
  const response = await axios.get(
    `${API_BACKEND_URL}/collections/${collectionId}/requirements?companyId=${companyId}`,
  );
  return response.data;
};

export const useApiRequirements = (collectionId, companyId, options = {}) =>
  useQuery({
    queryKey: ["apiRequirements", collectionId, companyId],
    queryFn: fetchRequirements,
    enabled: Boolean(collectionId && companyId),
    ...options,
  });

export const useUploadApiRequirement = (collectionId, companyId, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ file, name }) => {
      const form = new FormData();
      form.append("file", file);
      if (name) form.append("name", name);
      const response = await axios.post(
        `${API_BACKEND_URL}/collections/${collectionId}/requirements?companyId=${companyId}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } },
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["apiRequirements", collectionId, companyId] });
      options.onSuccess?.(data);
    },
    ...options,
  });
};

export const useDeleteApiRequirement = (collectionId, companyId, options = {}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requirementId) => {
      const response = await axios.delete(
        `${API_BACKEND_URL}/collections/${collectionId}/requirements/${requirementId}`
          + `?companyId=${companyId}`,
      );
      return response.data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["apiRequirements", collectionId, companyId] });
      options.onSuccess?.(data);
    },
    ...options,
  });
};
